import React, { useEffect, useRef, useState } from "react";
import { isStaleChunkError, reloadOnceForStaleChunk } from "../utils/staleChunkReload";
import { AlertTriangle, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { POS_RENDER_ERROR_EVENT, reportPosClientError } from "../observability/posErrorReporting";
import { HosOvState } from "./hos/overlays/HosOverlays";
import { ovText } from "./hos/overlays/hosOverlayText";
import { HOS_SHELL_ENABLED } from "./hos/shell/hosShellFlag";
import "../styles/hos/components/button.css";

type AppErrorBoundaryProps = React.PropsWithChildren<{
  title?: string;
  description?: string;
  resetKey?: string;
}>;

type AppErrorBoundaryState = {
  hasError: boolean;
  message?: string;
};

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "Unexpected application error.";
}

export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return {
      hasError: true,
      message: getErrorMessage(error),
    };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // A chunk from the previous deploy: reload once to load the new build (see staleChunkReload).
    if (isStaleChunkError(error) && reloadOnceForStaleChunk()) return;
    console.error("[Xentros render error]", error, info);
    window.dispatchEvent(new CustomEvent(POS_RENDER_ERROR_EVENT, {
      detail: {
        message: getErrorMessage(error),
        stackTrace: error instanceof Error ? error.stack : null,
        context: { componentStack: info.componentStack },
      },
    }));
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, message: undefined });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // New shell: a calm state (DESIGN.md §6) — muted icon, title, one line, the raw message
    // collapsed under "Technical details", Reload as the one primary action.
    if (HOS_SHELL_ENABLED) {
      return (
        <div className="hos-ui hos-ov-page">
          <HosOvState
            role="alert"
            tone="bad"
            icon={<AlertTriangle strokeWidth={1.5} />}
            title={this.props.title ?? "Something went wrong"}
            text={this.props.description ?? "This view could not be displayed correctly. The rest of Xentros is still available."}
            detail={this.state.message}
            detailLabel={ovText("errorDetails")}
            actions={<button type="button" className="hos-btn hos-btn--primary" onClick={this.handleReload}>{ovText("errorReload")}</button>}
          />
        </div>
      );
    }

    return (
      <div className="xErrorBoundary" role="alert">
        <div className="xErrorBoundaryIcon" aria-hidden="true">
          <AlertTriangle size={22} strokeWidth={1.9} />
        </div>
        <div className="xErrorBoundaryBody">
          <h1>{this.props.title ?? "Something went wrong"}</h1>
          <p>
            {this.props.description ??
              "This view could not be displayed correctly. The rest of Xentros is still available."}
          </p>
          <div className="xErrorBoundaryMessage">{this.state.message}</div>
          <div className="xErrorBoundaryActions">
            <button type="button" className="xErrorBoundaryButton xErrorBoundaryButton--primary" onClick={this.handleReload}>
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export function RouteErrorBoundary({ children }: React.PropsWithChildren) {
  const location = useLocation();

  return (
    <AppErrorBoundary
      resetKey={`${location.pathname}${location.search}`}
      title="This screen could not be displayed"
      description="This view could not be displayed correctly. Please reload the app."
    >
      {children}
    </AppErrorBoundary>
  );
}

type RuntimeErrorNotice = {
  id: number;
  message: string;
  // The screen the error happened on: the banner belongs to that screen only.
  pathname: string;
};

// A background error is not a blocking state: it clears itself after a while and
// on navigation instead of following the user across screens until dismissed.
const RUNTIME_ERROR_NOTICE_MS = 10_000;

function getPromiseRejectionMessage(reason: unknown) {
  return getErrorMessage(reason);
}

export function GlobalRuntimeErrorBanner() {
  const { hotelId, isAuthenticated } = useAuth();
  const location = useLocation();
  const [notice, setNotice] = useState<RuntimeErrorNotice | null>(null);
  // Router pathname (also correct under the Tauri HashRouter), read when an error fires.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => {
      setNotice((current) => (current?.id === notice.id ? null : current));
    }, RUNTIME_ERROR_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const recent = new Map<string, number>();
    const report = (kind: "window_error" | "unhandled_rejection" | "render_error", message: string, stackTrace?: string | null, context?: Record<string, unknown>) => {
      if (!isAuthenticated || !hotelId || !window.location.pathname.startsWith("/module/pos")) return;
      const signature = `${kind}:${message}:${stackTrace ?? ""}`;
      const now = Date.now();
      if ((recent.get(signature) ?? 0) > now - 5_000) return;
      recent.set(signature, now);
      void reportPosClientError(hotelId, { kind, message, stackTrace, context });
    };

    const showNotice = (message: string) => {
      setNotice({
        id: Date.now(),
        message,
        pathname: pathnameRef.current,
      });
    };

    const handleError = (event: ErrorEvent) => {
      const message = event.message || getErrorMessage(event.error);
      showNotice(message);
      report("window_error", message, event.error instanceof Error ? event.error.stack : null, {
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const message = getPromiseRejectionMessage(event.reason);
      showNotice(message);
      report("unhandled_rejection", message, event.reason instanceof Error ? event.reason.stack : null);
    };

    const handleRenderError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string; stackTrace?: string | null; context?: Record<string, unknown> }>).detail;
      report("render_error", detail?.message ?? "Unexpected render error.", detail?.stackTrace, detail?.context);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener(POS_RENDER_ERROR_EVENT, handleRenderError);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener(POS_RENDER_ERROR_EVENT, handleRenderError);
    };
  }, [hotelId, isAuthenticated]);

  if (!notice || notice.pathname !== location.pathname) return null;

  // New shell: a quiet toast bottom right (bad-bg chip colours, no shadow), same timeout and dismiss.
  if (HOS_SHELL_ENABLED) {
    return (
      <div className="hos-ui hos-ov-toast hos-ov-toast--bad" role="alert">
        <AlertTriangle className="hos-ov-toast__icon" aria-hidden="true" strokeWidth={1.8} />
        <div className="hos-ov-toast__text">
          <strong>{ovText("errorBackground")}</strong>
          <span>{notice.message}</span>
        </div>
        <button type="button" className="hos-btn hos-btn--icon hos-btn--30 hos-ov-toast__close" onClick={() => setNotice(null)} aria-label={ovText("errorDismiss")}>
          <X aria-hidden="true" strokeWidth={1.8} />
        </button>
      </div>
    );
  }

  return (
    <div className="xRuntimeErrorBanner" role="alert">
      <AlertTriangle size={18} strokeWidth={1.9} aria-hidden="true" />
      <div className="xRuntimeErrorBannerText">
        <strong>Background error</strong>
        <span>{notice.message}</span>
      </div>
      <button type="button" className="xRuntimeErrorDismiss" onClick={() => setNotice(null)} aria-label="Dismiss error">
        <X size={16} strokeWidth={1.9} />
      </button>
    </div>
  );
}
